/**
 * HeroKpiProgressBar — headline KPI tile with capacity bar + YoY arrow.
 *
 * Fork Tier-A-#1 (A.2). Inspired by nicelydone Mixpanel `6f2b5d4c`
 * ("Horizontal progress-bar metric"), re-skinned in Butler palette
 * (`midway-500` bar fill on `hairline` track; `marquee-500` up-arrow,
 * `ink-soft` flat, muted rose down-arrow).
 *
 * Display logic:
 *   - Big value (Montserrat, tabular-nums) — the current metric.
 *   - Capacity bar — only rendered when `capacity > 0`.
 *   - YoY arrow badge — only rendered when `yoyPct` is a finite number.
 *     `yoyPct === null` is the graceful-degrade case (endpoint 404'd or
 *     compare target not deployed) and silently hides the badge.
 *
 * Parent page owns the fetch — this tile is presentational so the same
 * component can render any of the 5 hero metrics (riders/hr, orders/hr,
 * guests, revenue, ride_gross) by swapping props.
 */

import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react'

export type HeroMetric =
  | 'riders_per_hr'
  | 'orders_per_hr'
  | 'guests'
  | 'revenue'
  | 'ride_gross'

const METRIC_LABELS: Record<HeroMetric, string> = {
  riders_per_hr: 'Riders / hr',
  orders_per_hr: 'Orders / hr',
  guests: 'Guests',
  revenue: 'Revenue',
  ride_gross: 'Ride Gross',
}

interface HeroKpiProgressBarProps {
  metric: HeroMetric
  /** Current numeric value (already in display units — dollars, not cents). */
  value: number | null
  /**
   * Benchmark / capacity for the progress bar denominator. `null` or
   * non-positive values hide the bar entirely (no misleading empty track).
   */
  capacity?: number | null
  /**
   * Year-over-year percentage change. `null` suppresses the arrow (degrade
   * path — YoY endpoint absent); finite numbers render the arrow. Positive
   * = up, negative = down, 0 = flat.
   */
  yoyPct?: number | null
  /** Valuer formatter — parent decides $ vs plain number. */
  formatValue: (n: number) => string
  /** Override the default label (rarely needed). */
  label?: string
  loading?: boolean
  'data-testid'?: string
}

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0
  if (n < 0) return 0
  if (n > 100) return 100
  return n
}

function computeFillPct(value: number | null, capacity: number | null | undefined): number {
  if (value === null || !Number.isFinite(value)) return 0
  if (!capacity || capacity <= 0 || !Number.isFinite(capacity)) return 0
  // Round to 2 decimals to avoid float-precision jitter like 62.25000000000001
  // leaking into the inline style attribute (reads noisy, breaks pixel-exact
  // snapshot testing).
  return Math.round(clampPct((value / capacity) * 100) * 100) / 100
}

interface YoYBadgeProps {
  pct: number
}

function YoYBadge({ pct }: YoYBadgeProps) {
  // Flat window: |change| < 0.5% is visually indistinguishable from zero
  // and operator-irrelevant. Render a neutral dash so we don't exaggerate
  // noise into a headline trend.
  const flat = Math.abs(pct) < 0.5
  const up = !flat && pct > 0
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight
  const skin = flat
    ? 'border-hairline bg-surface-stripe text-ink-soft dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300'
    : up
      ? 'border-marquee-200 bg-marquee-50 text-marquee-700 dark:border-marquee-700 dark:bg-marquee-900/30 dark:text-marquee-200'
      : 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-900/30 dark:text-rose-200'

  const direction = flat ? 'flat' : up ? 'up' : 'down'
  const formatted = `${up ? '+' : ''}${pct.toFixed(1)}%`

  return (
    <span
      data-testid="hero-yoy-badge"
      data-direction={direction}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${skin}`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      <span>{formatted}</span>
      <span className="text-[10px] font-normal opacity-80">vs last year</span>
    </span>
  )
}

export function HeroKpiProgressBar({
  metric,
  value,
  capacity,
  yoyPct,
  formatValue,
  label,
  loading = false,
  'data-testid': testId,
}: HeroKpiProgressBarProps) {
  const resolvedTestId = testId ?? `hero-kpi-${metric}`
  const resolvedLabel = label ?? METRIC_LABELS[metric]

  if (loading) {
    return (
      <div
        data-testid={resolvedTestId}
        data-state="loading"
        aria-busy="true"
        aria-label={`Loading ${resolvedLabel}`}
        className="rounded-card border border-hairline bg-white p-5 shadow-card-soft dark:border-slate-700 dark:bg-slate-800"
      >
        <div className="h-3 w-24 animate-pulse rounded bg-hairline dark:bg-slate-700" />
        <div className="mt-3 h-10 w-40 animate-pulse rounded bg-hairline dark:bg-slate-700" />
        <div className="mt-4 h-2 w-full animate-pulse rounded bg-hairline dark:bg-slate-700" />
      </div>
    )
  }

  const showValue = value !== null && Number.isFinite(value)
  const fillPct = computeFillPct(value, capacity)
  const showBar = capacity !== null && capacity !== undefined && capacity > 0
  const showYoy = typeof yoyPct === 'number' && Number.isFinite(yoyPct)

  return (
    <div
      data-testid={resolvedTestId}
      data-state={showValue ? 'ready' : 'empty'}
      data-metric={metric}
      className="group relative overflow-hidden rounded-card border border-butler-200 bg-butler-50 p-5 shadow-card-soft transition hover:shadow-card hover:-translate-y-0.5 dark:border-butler-700 dark:bg-butler-900/30"
    >
      {/* Butler sparkle rule — subtle brand seasoning along the top edge. */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-[3px] bg-butler-sparkle opacity-70 group-hover:opacity-100 transition-opacity"
      />
      <div className="flex items-start justify-between gap-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-butler-700 dark:text-butler-200">
          {resolvedLabel}
        </div>
        {showYoy ? <YoYBadge pct={yoyPct as number} /> : null}
      </div>
      <div
        data-testid={`${resolvedTestId}-value`}
        className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-butler-800 tabular-nums dark:text-butler-50"
      >
        {showValue ? formatValue(value as number) : '—'}
      </div>
      {showBar ? (
        <>
          <div
            role="progressbar"
            aria-valuenow={Math.round(fillPct)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${resolvedLabel} progress toward capacity`}
            className="mt-4 h-2 w-full overflow-hidden rounded-full bg-hairline dark:bg-slate-700"
          >
            <div
              data-testid={`${resolvedTestId}-fill`}
              className="h-full rounded-full bg-midway-500 transition-[width] duration-300"
              style={{ width: `${fillPct}%` }}
            />
          </div>
          <div className="mt-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-ink-soft dark:text-slate-400">
            <span>{Math.round(fillPct)}% of capacity</span>
            <span>Cap {formatValue(capacity as number)}</span>
          </div>
        </>
      ) : null}
    </div>
  )
}
